import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/Button.js";
import { Badge } from "../../ui/Badge.js";
import { LoadingBlock } from "../../ui/Spinner.js";
import { EmptyState } from "../../ui/EmptyState.js";
import { formatRelative } from "../../lib/format.js";
import {
  listWarehouses,
  getRuns,
  getOrphans,
  triggerSync,
  patchStock,
  type WarehouseOverview,
  type WarehouseSyncRun,
  type OrphanRow,
} from "../../lib/warehousesAdminApi.js";
import {
  PackageIcon,
  StoreIcon,
  AlertIcon,
  CopyIcon,
  InfoIcon,
} from "../../lib/icons.js";

/**
 * Admin /admin/warehouses — přehled skladů + orphan management.
 *
 * Flow:
 *  - TopList: přehled všech skladů (stats, last sync, match rate)
 *  - Detail (když klik na sklad): tabs — Orphans / Historie / Přehled
 */
export function WarehousesAdminPage(): JSX.Element {
  const [warehouses, setWarehouses] = useState<WarehouseOverview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listWarehouses()
      .then((d) => !cancelled && setWarehouses(d.warehouses))
      .catch((e) => !cancelled && setError(String(e)));
    // poll statusu každých 15s
    const iv = setInterval(() => {
      listWarehouses()
        .then((d) => !cancelled && setWarehouses(d.warehouses))
        .catch(() => undefined);
    }, 15_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [refreshKey]);

  const selected = useMemo(
    () => warehouses?.find((w) => w.code === selectedCode) ?? null,
    [warehouses, selectedCode],
  );

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  }

  if (!warehouses) return <LoadingBlock label="Načítám sklady…" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Sklady
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Fyzické sklady (KTM Vlašim, partneři, dodavatelé) — stav, sync runy,
          orphans.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {warehouses.map((w) => (
          <WarehouseCard
            key={w.code}
            wh={w}
            selected={w.code === selectedCode}
            onSelect={() => setSelectedCode(w.code)}
            onSyncComplete={() => setRefreshKey((k) => k + 1)}
          />
        ))}
      </section>

      {selected && (
        <WarehouseDetail
          key={selected.code + "-" + refreshKey}
          wh={selected}
          onDataChanged={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

/* ────────────────────────── Card ────────────────────────── */

function WarehouseCard({
  wh,
  selected,
  onSelect,
  onSyncComplete,
}: {
  wh: WarehouseOverview;
  selected: boolean;
  onSelect: () => void;
  onSyncComplete: () => void;
}): JSX.Element {
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const rate = wh.stats.matchRatePct;
  const isRunning = wh.lastRun?.status === "running";

  async function handleSync(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    setSyncError(null);
    setSyncing(true);
    try {
      await triggerSync(wh.code);
      setTimeout(onSyncComplete, 2000);
    } catch (err) {
      setSyncError(String(err));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div
      onClick={onSelect}
      className={`rounded-2xl border bg-white shadow-card p-5 cursor-pointer transition-colors ${
        selected ? "border-ktm-orange ring-2 ring-ktm-orange-100" : "border-zinc-200 hover:border-zinc-300"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <StoreIcon size={16} />
            </div>
            <h2 className="text-base font-semibold text-zinc-900 truncate">{wh.name}</h2>
            <Badge tone={wh.active ? "green" : "neutral"}>
              {wh.active ? "Aktivní" : "Neaktivní"}
            </Badge>
            <Badge tone="neutral">{wh.kind}</Badge>
          </div>
          <div className="mt-1 text-xs text-zinc-500 font-mono">{wh.code}</div>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleSync}
          disabled={syncing || isRunning}
        >
          {isRunning ? "Běží…" : syncing ? "Spouštím…" : "Sync"}
        </Button>
      </div>

      {syncError && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
          {syncError}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="SKU celkem" value={wh.stats.totalSku.toLocaleString("cs-CZ")} />
        <Stat
          label="Match rate"
          value={rate !== null ? `${rate}%` : "—"}
          tone={rate !== null && rate >= 80 ? "good" : rate !== null && rate >= 60 ? "warn" : "bad"}
        />
        <Stat
          label="Orphans"
          value={wh.stats.unmatched.toLocaleString("cs-CZ")}
          tone={wh.stats.unmatched > 0 ? "warn" : "good"}
        />
        <Stat
          label="Ignorováno"
          value={wh.stats.ignored.toLocaleString("cs-CZ")}
          tone="neutral"
        />
      </div>

      <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between gap-3 text-xs text-zinc-500">
        <div>
          Poslední sync:{" "}
          {wh.lastSyncAt ? (
            <span className="font-medium text-zinc-700">{formatRelative(wh.lastSyncAt)}</span>
          ) : (
            "nikdy"
          )}
        </div>
        {wh.lastRun && <RunStatusPill run={wh.lastRun} />}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "good" | "warn" | "bad" | "neutral";
}): JSX.Element {
  const colors = {
    good: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-red-700",
    neutral: "text-zinc-900",
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
        {label}
      </div>
      <div className={`mt-0.5 text-base font-semibold ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function RunStatusPill({ run }: { run: WarehouseSyncRun }): JSX.Element {
  const map = {
    success: { tone: "green" as const, label: "OK" },
    error: { tone: "red" as const, label: "Error" },
    running: { tone: "amber" as const, label: "Běží" },
    aborted_suspicious: { tone: "red" as const, label: "Abort: suspicious" },
  };
  const cfg = map[run.status as keyof typeof map] ?? { tone: "neutral" as const, label: run.status };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
}

/* ────────────────────────── Detail ────────────────────────── */

function WarehouseDetail({
  wh,
  onDataChanged,
}: {
  wh: WarehouseOverview;
  onDataChanged: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<"orphans" | "runs" | "info">("orphans");

  return (
    <section className="bg-white rounded-2xl border border-zinc-200 shadow-card">
      <div className="border-b border-zinc-100 px-5 py-3 flex items-center gap-4">
        <h2 className="text-sm font-semibold text-zinc-900">
          Detail: <span className="font-normal text-zinc-600">{wh.name}</span>
        </h2>
        <div className="flex gap-1 ml-auto">
          <TabButton active={tab === "orphans"} onClick={() => setTab("orphans")}>
            Orphans ({wh.stats.unmatched.toLocaleString("cs-CZ")})
          </TabButton>
          <TabButton active={tab === "runs"} onClick={() => setTab("runs")}>
            Historie
          </TabButton>
          <TabButton active={tab === "info"} onClick={() => setTab("info")}>
            Info
          </TabButton>
        </div>
      </div>
      <div className="p-5">
        {tab === "orphans" && <OrphansTab code={wh.code} onChanged={onDataChanged} />}
        {tab === "runs" && <RunsTab code={wh.code} />}
        {tab === "info" && <InfoTab wh={wh} />}
      </div>
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
        active ? "bg-ktm-orange text-white" : "text-zinc-600 hover:bg-zinc-100"
      }`}
    >
      {children}
    </button>
  );
}

/* ────────────────────────── Orphans tab ────────────────────────── */

function OrphansTab({
  code,
  onChanged,
}: {
  code: string;
  onChanged: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<OrphanRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"unmatched" | "ignored">("unmatched");
  const [onlyStock, setOnlyStock] = useState(true);
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOrphans(code, { limit: LIMIT, offset, q, status, onlyStock })
      .then((d) => {
        if (cancelled) return;
        setRows(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setRows([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [code, offset, q, status, onlyStock]);

  // reset offset při změně filtru
  useEffect(() => {
    setOffset(0);
  }, [q, status, onlyStock]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Hledat kód nebo název…"
          className="h-9 px-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-64"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "unmatched" | "ignored")}
          className="h-9 px-3 rounded-md border border-zinc-200 text-sm"
        >
          <option value="unmatched">Unmatched</option>
          <option value="ignored">Ignorované</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={onlyStock}
            onChange={(e) => setOnlyStock(e.target.checked)}
          />
          Jen s fyzickým skladem
        </label>
        <div className="ml-auto text-xs text-zinc-500">
          {total.toLocaleString("cs-CZ")} výsledků
        </div>
      </div>

      {loading && !rows ? (
        <LoadingBlock label="Načítám…" />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={24} />}
          title="Žádné orphans"
          description="Všechny SKU jsou spárované."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-200">
                <th className="text-left py-2 px-2 font-medium">Kód</th>
                <th className="text-left py-2 px-2 font-medium">Název</th>
                <th className="text-right py-2 px-2 font-medium">Sklad</th>
                <th className="text-right py-2 px-2 font-medium">Cena</th>
                <th className="text-right py-2 px-2 font-medium">Akce</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <OrphanRowView
                  key={r.id}
                  code={code}
                  row={r}
                  onChanged={() => {
                    setRows((prev) =>
                      prev ? prev.filter((x) => x.id !== r.id) : prev,
                    );
                    onChanged();
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > LIMIT && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z {total}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function OrphanRowView({
  code,
  row,
  onChanged,
}: {
  code: string;
  row: OrphanRow;
  onChanged: () => void;
}): JSX.Element {
  const [assigning, setAssigning] = useState(false);
  const [assignValue, setAssignValue] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleAction(
    action: "assign" | "ignore" | "reset",
    articleNumber?: string,
  ): Promise<void> {
    setErr(null);
    setPending(true);
    try {
      await patchStock(code, row.id, action, articleNumber);
      onChanged();
    } catch (e) {
      setErr(String(e));
    } finally {
      setPending(false);
    }
  }

  const sellable = row.qtyAvailable + row.qtyInbound;

  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/50 align-top">
      <td className="py-2 px-2 font-mono text-xs">
        <div className="flex items-center gap-1">
          <span>{row.supplierCode}</span>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(row.supplierCode)}
            className="text-zinc-400 hover:text-ktm-orange"
            title="Kopírovat"
          >
            <CopyIcon size={12} />
          </button>
        </div>
      </td>
      <td className="py-2 px-2 max-w-xs">
        <div className="truncate" title={row.name ?? ""}>
          {row.name ?? "—"}
        </div>
        {row.matchStatus === "ignored" && (
          <Badge tone="neutral">Ignorováno</Badge>
        )}
      </td>
      <td className="py-2 px-2 text-right tabular-nums">
        <div className={sellable > 0 ? "font-semibold text-emerald-700" : "text-zinc-400"}>
          {sellable}
        </div>
        {row.qtyInbound > 0 && (
          <div className="text-[10px] text-amber-700">+{row.qtyInbound} v příjmu</div>
        )}
      </td>
      <td className="py-2 px-2 text-right tabular-nums text-zinc-600">
        {row.price ? `${parseFloat(row.price).toLocaleString("cs-CZ")} Kč` : "—"}
      </td>
      <td className="py-2 px-2 text-right">
        {assigning ? (
          <div className="flex items-center gap-1 justify-end">
            <input
              value={assignValue}
              onChange={(e) => setAssignValue(e.target.value.trim())}
              placeholder="article_number"
              className="h-7 px-2 rounded-md border border-zinc-300 text-xs font-mono w-36"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && assignValue) handleAction("assign", assignValue);
                if (e.key === "Escape") setAssigning(false);
              }}
            />
            <Button
              size="sm"
              disabled={!assignValue || pending}
              onClick={() => handleAction("assign", assignValue)}
            >
              OK
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAssigning(false)}>
              ×
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1 justify-end">
            {row.matchStatus === "ignored" ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={pending}
                onClick={() => handleAction("reset")}
              >
                Obnovit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="secondary" onClick={() => setAssigning(true)}>
                  Přiřadit…
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={pending}
                  onClick={() => handleAction("ignore")}
                >
                  Ignorovat
                </Button>
              </>
            )}
          </div>
        )}
        {err && <div className="mt-1 text-[10px] text-red-700">{err}</div>}
      </td>
    </tr>
  );
}

/* ────────────────────────── Runs tab ────────────────────────── */

function RunsTab({ code }: { code: string }): JSX.Element {
  const [runs, setRuns] = useState<WarehouseSyncRun[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getRuns(code, 50)
      .then((d) => !cancelled && setRuns(d.runs))
      .catch(() => !cancelled && setRuns([]));
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!runs) return <LoadingBlock label="Načítám historii…" />;
  if (runs.length === 0) return <div className="text-sm text-zinc-500">Zatím bez běhů.</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-200">
            <th className="text-left py-2 px-2 font-medium">Kdy</th>
            <th className="text-left py-2 px-2 font-medium">Trigger</th>
            <th className="text-left py-2 px-2 font-medium">Status</th>
            <th className="text-right py-2 px-2 font-medium">SKU</th>
            <th className="text-right py-2 px-2 font-medium">Matched</th>
            <th className="text-right py-2 px-2 font-medium">Orphans</th>
            <th className="text-right py-2 px-2 font-medium">Deaktiv.</th>
            <th className="text-left py-2 px-2 font-medium">Chyba</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100">
              <td className="py-2 px-2 text-xs text-zinc-700" title={r.startedAt}>
                {formatRelative(r.startedAt)}
              </td>
              <td className="py-2 px-2 text-xs text-zinc-500">{r.trigger}</td>
              <td className="py-2 px-2">
                <RunStatusPill run={r} />
              </td>
              <td className="py-2 px-2 text-right tabular-nums text-zinc-700">
                {r.rowsTotal ?? "—"}
              </td>
              <td className="py-2 px-2 text-right tabular-nums text-emerald-700">
                {r.rowsMatched ?? "—"}
              </td>
              <td className="py-2 px-2 text-right tabular-nums text-amber-700">
                {r.rowsOrphaned ?? "—"}
              </td>
              <td className="py-2 px-2 text-right tabular-nums text-zinc-500">
                {r.rowsDeactivated ?? "—"}
              </td>
              <td className="py-2 px-2 text-xs text-red-700 max-w-md truncate" title={r.errorMessage ?? ""}>
                {r.errorMessage ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────── Info tab ────────────────────────── */

function InfoTab({ wh }: { wh: WarehouseOverview }): JSX.Element {
  return (
    <div className="space-y-4 max-w-2xl">
      <InfoRow label="Kód" value={<span className="font-mono">{wh.code}</span>} />
      <InfoRow label="Název" value={wh.name} />
      <InfoRow label="Typ" value={wh.kind} />
      <InfoRow label="Země" value={wh.country ?? "—"} />
      <InfoRow label="Měna" value={wh.currency} />
      <InfoRow
        label="Typ ceny"
        value={
          wh.priceKind === "moc_net"
            ? "MOC bez DPH"
            : wh.priceKind === "moc_gross"
              ? "MOC s DPH"
              : wh.priceKind === "nc"
                ? "Nákupní"
                : wh.priceKind
        }
      />
      <InfoRow label="Feed formát" value={wh.feedFormat ?? "—"} />
      <InfoRow
        label="Feed URL"
        value={
          wh.feedUrl ? (
            <a
              href={wh.feedUrl}
              target="_blank"
              rel="noreferrer"
              className="text-ktm-orange hover:underline break-all"
            >
              {wh.feedUrl}
            </a>
          ) : (
            "—"
          )
        }
      />
      {wh.notes && <InfoRow label="Poznámky" value={wh.notes} />}

      <div className="pt-3 border-t border-zinc-100 flex items-start gap-2 text-xs text-zinc-500">
        <InfoIcon size={14} className="mt-0.5 flex-shrink-0" />
        <p>
          Sklad je nositel pravdy o fyzickém stavu. Sync běží automaticky 4× denně
          (00:37, 06:33, 12:33, 18:33). Ruční sync kdykoliv přes tlačítko výše.
        </p>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 items-start text-sm">
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium pt-0.5">
        {label}
      </div>
      <div className="text-zinc-800">{value}</div>
    </div>
  );
}
